import { call, put, takeEvery, takeLatest, select, debounce } from "redux-saga/effects";
import { getLocation, push, LOCATION_CHANGE } from "connected-react-router";
import { matchPath } from 'connected-react-router';
import { isEqual } from "lodash";
import { actions, constants, selector } from "./redux";
import { getFilterByLocationSearch, getQueryByFilter } from "./urlFilters";
import { actions as crewUserActions } from "../crew-users/redux";
import { actions as commentActions } from "../comments/redux";
import Api from "../../utils/Api";
import {
  addItem as notify,
  addSuccess,
  addError,
} from "../notifications/actions";

const basePageUrl = "/task-manager";

export function* fetchItems() {
  try {
    const featureState = yield select(selector);
    const { data } = yield call(Api.taskManager.index, featureState);

    yield put(actions.addItems(data));
  } catch (e) {
    console.error(e);

    yield put(actions.loadItemsError(e));
  }
}

export function* clearSubtasks() {
  try {
    yield put(actions.addSubtasks({}));
  } catch (e) {
    console.error(e);
  }
}

export function* fetchSubtasks({ payload: id }) {
  try {
    const { data } = yield call(Api.taskManager.subtasks, id);

    yield put(actions.addSubtasks(data));
  } catch (e) {
    console.error(e);

    yield put(actions.loadItemsError(e));
  }
}

export function* createItem({ payload: { form: payload, cb, errorCb } }) {
  try {
    const { data } = yield call(Api.taskManager.create, payload);

    yield put(actions.addCreatedItem(data));
    // CREW-1268 — through the watcher, so a newer page/sort request cancels it
    // (and it cancels an older one) instead of racing it.
    yield put(actions.loadItemsRequest());
    yield put(
      notify({
        type: "success",
        title: "Success",
        message: "The task created successfully",
      })
    );
    yield call(cb);
  } catch (e) {
    yield put(actions.createItemError(e));
    console.error(e);
    yield put(
      addError(
        e.apiErrors[0].detail || "Error",
        e.apiErrors[0].title || "Internal error"
      )
    );
    yield call(errorCb);
  }
}

function* updateItem({ payload: { form: payload, cb } }) {
  try {
    yield call(Api.taskManager.update, payload);

    yield put(actions.loadItemsRequest());
    yield put(addSuccess("Task updated successfully"));
  } catch (e) {
    console.error(e);
    yield put(actions.updateItemError(e));
  } finally {
    yield call(cb);
  }
}

function* updateStatus({ payload: { form, cb, interactionId = "" } }) {
  try {
    yield call(Api.taskManager.updateStatus, form);
    interactionId
      ? yield put(commentActions.fetchTasksForInteraction({ interactionId }))
      : yield put(actions.loadItemsRequest());

    yield put(addSuccess("Task status updated successfully"));
  } catch (e) {
    console.error(e);
    yield put(
      addError("Check all conditions to finish task", "Status update failed")
    );
  } finally {
    yield call(cb);
  }
}

// A2: the URL query string is the single source of truth for TM filters
// (variant 1 — empty URL = empty filter). On every location change that
// lands on the TM page, hydrate redux from the URL and fetch. This covers
// first render, in-app navigation, back/forward, task deep links
// (?task_id=N → {search: "task=N"} ONLY, so no other filter hides the
// task — A2a) and our own pushes from syncFilterToUrl.
function* fetchItemsPageInit({
  payload: {
    isFirstRendering,
    location: { pathname, search },
  },
}) {
  const didOpenPage =
    matchPath(pathname, { exact: true, path: `${basePageUrl}` }) ||
    // "|| isFirstRendering" for case when you open a form and reload the page
    // and we MUST show list items behind the form
    (isFirstRendering &&
      matchPath(pathname, { path: `${basePageUrl}/:id?/(new|edit|delete)` }));

  if (!didOpenPage) return;

  const urlFilter = getFilterByLocationSearch(search);
  const { appliedFilter } = yield select(selector);

  // Only replace the filter (and reset paging) when the URL actually
  // differs — e.g. closing an edit modal lands back on the same query
  // and must keep the current page.
  if (!isEqual(urlFilter, appliedFilter || {})) {
    yield put(actions.setFilter(urlFilter));
  }
  yield put(actions.loadItemsRequest());
  yield put(crewUserActions.loadUsersForMentionsRequest());
}

// Filter interactions never fetch directly anymore: APPLY_FILTER
// serializes the just-applied filter into the URL and the resulting
// LOCATION_CHANGE hydrates + fetches (single code path). When the URL
// already encodes the same filter (re-applying an identical one), no
// navigation happens, so fetch here — APPLY_FILTER's reducer has
// already set isLoading. The comparison is on PARSED filters, not raw
// strings, because the same state can be spelled differently
// (?task_id=N vs ?search=task%3DN).
function* syncFilterToUrl() {
  try {
    const location = yield select(getLocation);
    if (!matchPath(location.pathname, { exact: true, path: basePageUrl }))
      return;

    const { filter } = yield select(selector);
    const query = getQueryByFilter(filter);

    if (
      isEqual(
        getFilterByLocationSearch(location.search),
        getFilterByLocationSearch(`?${query}`)
      )
    ) {
      yield put(actions.loadItemsRequest());
      return;
    }

    yield put(push(query ? `${basePageUrl}?${query}` : basePageUrl));
  } catch (e) {
    console.error(e);
  }
}

function* createVacancyApply({ payload: { vacancyApplyForm, cb } }) {
  try {
    yield call(Api.vacancyApplies.create, vacancyApplyForm);

    yield put(actions.loadItemsRequest());

    yield put(
      notify({
        type: "success",
        title: "Success",
        message: "Your Apply created successfully",
      })
    );
  } catch (e) {
    yield put(actions.createItemError(e));
    console.error(e);

    yield put(
      addError(
        e.apiErrors[0].detail || "Error",
        e.apiErrors[0].title || "Internal error"
      )
    );
  } finally {
    yield call(cb);
  }
}

function* updateVacancyApplyStatus({ payload }) {
  try {
    yield call(Api.vacancyApplies.updateStatus, payload);

    yield put(actions.loadItemsRequest());

    yield put(addSuccess("Status updated successfully"));
  } catch (e) {
    console.error(e);
    yield put(actions.updateItemError(e));

    console.error(e);
  }
}

/**
 * getFeatureSagas (rename)
 * each saga file should return all feature sagas
 */
export default function getFeatureSagas() {
  return [
    // takeLatest (CREW-1268): a newer request cancels the one in flight, so an
    // older answer arriving last cannot overwrite the list.
    takeLatest(
      [
        constants.CHANGE_PAGE_NUMBER,
        constants.LOAD_ITEMS_REQUEST,
        constants.SORT_WITH_DIRECTION,
      ],
      fetchItems
    ),
    // A2: applying filters updates the URL; the LOCATION_CHANGE handler
    // (fetchItemsPageInit) is the single place that fetches for it.
    takeEvery(constants.APPLY_FILTER, syncFilterToUrl),
    takeEvery(constants.CREATE_ITEM_REQUEST, createItem),
    takeEvery(LOCATION_CHANGE, fetchItemsPageInit),
    takeEvery(constants.UPDATE_ITEM_REQUEST, updateItem),
    takeEvery(constants.UPDATE_STATUS, updateStatus),
    debounce(50, constants.FETCH_SUBTASKS, clearSubtasks),
    debounce(250, constants.FETCH_SUBTASKS, fetchSubtasks),
    takeEvery(constants.CREATE_VACANCY_APPLY, createVacancyApply),
    takeEvery(constants.UPDATE_APPLY_STATUS, updateVacancyApplyStatus),
  ];
}
